"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { AiFillStar } from "react-icons/ai";
import { FaQuoteLeft } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { getTestimonialsData } from "@/data/testimonials";
import { useLanguage, useTranslations } from "@/lib/i18n";
import { COLORS } from "@/lib/design-tokens";

import "swiper/css";
import "swiper/css/pagination";

import Link from "next/link";
import { HiArrowRight } from "react-icons/hi";
import TestimonialCard from "@/components/shared/TestimonialCard";

export default function Testimonials() {
  const { language } = useLanguage();
  const t = useTranslations();
  const testimonialsData = getTestimonialsData(language);

  return (
    <section className="py-24 md:py-32 bg-[#F9F9F9] overflow-hidden">
      <div className="container mx-auto px-6 md:px-12">
        {/* HEADER SECTION */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex justify-center items-center gap-2 mb-4">
              <div className="flex text-brand-gold">
                {[...Array(5)].map((_, i) => (
                  <AiFillStar key={i} size={16} className="text-brand-gold" />
                ))}
              </div>
              <span className="text-sm font-bold text-gray-500 tracking-wide">
                4.9/5 • {language === "id" ? "PERINGKAT MEWAH" : "LUXURY RATED"}
              </span>
            </div>

            <h2 className="font-serif text-4xl md:text-5xl text-brand-charcoal mb-6">
              {t.testimonials.title}
            </h2>
            <p className="text-gray-500 font-light text-base md:text-lg">
              {t.testimonials.subtitle}
            </p>
          </motion.div>
        </div>

        {/* SWIPER CONTAINER */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={30}
            slidesPerView={1}
            loop={true}
            speed={1000}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{
              clickable: true,
              el: ".testimonial-pagination",
            }}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="pb-12"
          >
            {testimonialsData.map((review) => (
              <SwiperSlide key={review.id} className="h-auto pb-2">
                <TestimonialCard review={review} />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Pagination Container */}
          <div className="testimonial-pagination flex justify-center gap-2 mt-8 mb-12" />

          {/* View All Button */}
          <div className="text-center">
            <Link
              href="/testimonials"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white border border-gray-200 text-brand-charcoal hover:bg-brand-charcoal hover:text-white hover:border-brand-charcoal transition-all duration-300 rounded-full font-sans text-sm tracking-widest uppercase group"
            >
              {language === "id" ? "Lihat Semua Ulasan" : "View All Reviews"}
              <HiArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>
      </div>

      <style jsx global>{`
        .testimonial-pagination .swiper-pagination-bullet {
          width: 10px;
          height: 10px;
          background: #ddd;
          opacity: 1;
          transition: all 0.3s;
        }
        .testimonial-pagination .swiper-pagination-bullet-active {
          background: #8e7c5e;
          transform: scale(1.2);
        }
        /* Force Equal Height */
        .swiper-wrapper {
          align-items: stretch;
        }
        .swiper-slide {
          height: auto !important;
          display: flex;
          height: 100%;
        }
      `}</style>
    </section>
  );
}
